import { Suspense } from 'react';
import { Route } from 'react-router-dom';
import { FeatureProtectedRoute } from '@/components/FeatureProtectedRoute';
import { PageLoader } from './PageLoader';
import { lazyWithReload } from '@/lib/lazyWithReload';
import { COLAS, type ColaDeOperaciones } from '@/lib/operations/colasDeOperaciones';
import { precargarCola } from '@/routes/loaders/opsVentas';

/**
 * La ruta de una cola de Operaciones, armada entera. No se puede construir a medias.
 *
 * 🚨 POR QUÉ UNA FÁBRICA Y NO UN GATE. Un gate revisa DESPUÉS y avisa; una API bien hecha no deja
 *    escribir el error. Las cinco rutas de colas estaban escritas a mano, cada una repitiendo el
 *    mismo `loader` + `Suspense` + `FeatureProtectedRoute` + página perezosa. Cuatro de las cinco
 *    pasaron meses SIN su `loader` —nadie se acordó— y por eso sólo Ventas tenía precarga.
 *
 *    Acá el `loader` no es algo que hay que acordarse de poner: es lo único que esta función sabe
 *    hacer. Una cola nueva no puede quedar sin precarga porque no hay forma de registrarla sin ella.
 *
 * 🪤 Y EL COMPONENTE PEREZOSO SALE DE LA MISMA DECLARACIÓN que usa la precarga. Antes había dos
 *    listas de las mismas cinco páginas —`lazyPages.ts` y el mapa del loader— y mantenerlas de
 *    acuerdo dependía de la memoria de quien tocara una.
 *
 * 🪤 `lazyWithReload` y no `lazy` crudo: el día del deploy una pestaña abierta pide un chunk que ya
 *    no existe, y sin la recarga la vista entera cae al ErrorBoundary con un cartel que ni siquiera
 *    nombra la sección correcta.
 */

/** Un componente perezoso por cola, creado UNA vez (no en cada render). */
const PANTALLAS = Object.fromEntries(
  (Object.keys(COLAS) as ColaDeOperaciones[]).map((cola) => [cola, lazyWithReload(COLAS[cola].pagina)]),
) as Record<ColaDeOperaciones, ReturnType<typeof lazyWithReload>>;

/**
 * Devuelve el `<Route>` completo de una cola.
 *
 * Se usa como `{rutaDeCola('ventas', ROUTES.OPS_VENTAS, storeSegment)}` dentro del árbol de rutas.
 * El `path` viaja aparte porque las rutas del panel se montan dos veces —con y sin el segmento de
 * tienda— y esa decisión es del árbol, no de la cola.
 */
export function rutaDeCola(
  cola: ColaDeOperaciones,
  path: string,
): React.ReactElement {
  const Pantalla = PANTALLAS[cola];
  return (
    <Route
      key={path}
      path={path}
      // La precarga NO es opcional: pedir el código y los datos en paralelo es la razón de ser
      // de esta fábrica. Ver `precargarCola`.
      loader={() => precargarCola(cola)}
      element={(
        <FeatureProtectedRoute feature="operaciones">
          <Suspense fallback={<PageLoader variant="content" />}>
            <Pantalla />
          </Suspense>
        </FeatureProtectedRoute>
      )}
    />
  );
}
